// src/components/ParcelManagement/ParcelManagement.jsx
import React, { useState, useEffect } from 'react';
import { RiTruckLine, RiSearchLine, RiFilterLine, RiArrowLeftLine, RiArrowRightLine } from 'react-icons/ri';
import './ParcelManagement.css';
import { BASE_URL } from '../utils/constants';

//here

const ParcelManagement = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null); // for edit modal
  const [newDeliveryStatus, setNewDeliveryStatus] = useState('');
  const [newEstimatedDate, setNewEstimatedDate] = useState('');

  const itemsPerPage = 10;

  // Fetch all orders
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${BASE_URL}/api/orders`);
        if (!response.ok) throw new Error(`Failed: ${response.status}`);

        const data = await response.json();
        setOrders(data);
        setFilteredOrders(data);
      } catch (err) {
        console.error('Parcel fetch error:', err);
        setError('Unable to load parcel data.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Search filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOrders(orders);
      return;
    }

    const term = searchTerm.toLowerCase();
    const results = orders.filter(order => 
      order.reference?.toLowerCase().includes(term) ||
      order.phone?.toLowerCase().includes(term) ||
      order.accountName?.toLowerCase().includes(term) ||
      order.deliveryAddress?.fullName?.toLowerCase().includes(term)
    );

    setFilteredOrders(results);
    setCurrentPage(1);
  }, [searchTerm, orders]);

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

  // Open edit modal
  const openEditModal = (order) => {
    setSelectedOrder(order);
    setNewDeliveryStatus(order.deliveryStatus || 'pending');
    setNewEstimatedDate(order.estimatedDeliveryDate ? new Date(order.estimatedDeliveryDate).toISOString().split('T')[0] : '');
  };

  // Close modal
  const closeModal = () => {
    setSelectedOrder(null);
    setNewDeliveryStatus('');
    setNewEstimatedDate('');
  };

  // Update delivery status
  const handleUpdateDelivery = async () => {
    if (!selectedOrder) return;

    try {
      const response = await fetch(`${BASE_URL}/api/orders/${selectedOrder.reference}/delivery`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deliveryStatus: newDeliveryStatus,
          estimatedDeliveryDate: newEstimatedDate || undefined,
          // updatedBy: 'admin@example.com' — add auth later
        })
      });

      if (!response.ok) throw new Error('Update failed');

      const updatedOrder = await response.json();

      // Update local state
      setOrders(prev => prev.map(o => o.reference === updatedOrder.reference ? updatedOrder : o));
      setFilteredOrders(prev => prev.map(o => o.reference === updatedOrder.reference ? updatedOrder : o));

      closeModal();
      alert('Delivery status updated successfully!');
    } catch (err) {
      console.error('Update error:', err);
      alert('Failed to update delivery status.');
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'delivered': return 'status-delivered';
      case 'shipped': return 'status-shipped';
      case 'processing': return 'status-processing';
      case 'pending': return 'status-pending';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-pending';
    }
  };

  if (loading) return <div className="loading">Loading parcels...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="parcel-management-page">
      <header className="admin-header">
        <div className="logo-section">
          <div className="logo-icon">
            <RiTruckLine size={24} color="white" />
          </div>
          <span className="logo-text">Parcel Management</span>
        </div>

        <div className="header-actions">
          <button className="action-btn">
            {/* <RiDownloadLine size={18} /> Export Parcels */}
            <RiTruckLine size={18} /> Export Parcels
          </button>
        </div>
      </header>

      <main className="parcel-main">
        <div className="filters-bar">
          <div className="search-container">
            <RiSearchLine className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search by reference, phone, customer or address..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="filter-btn">
            <RiFilterLine size={18} /> Filters
          </button>
        </div>

        <div className="table-container">
          <table className="parcels-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Customer / Phone</th>
                <th>Delivery Status</th>
                <th>Est. Delivery</th>
                <th>Items</th>
                <th>Total (ZMK)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                    No parcels found {searchTerm ? `matching "${searchTerm}"` : ''}
                  </td>
                </tr>
              ) : (
                paginatedOrders.map(order => (
                  <tr key={order.reference}>
                    <td className="order-id">{order.reference}</td>
                    <td>
                      {order.accountName || 'Unknown'}<br />
                      <small>{order.phone}</small>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(order.deliveryStatus)}`}>
                        {order.deliveryStatus.charAt(0).toUpperCase() + order.deliveryStatus.slice(1)}
                      </span>
                    </td>
                    <td>
                      {order.estimatedDeliveryDate 
                        ? new Date(order.estimatedDeliveryDate).toLocaleDateString() 
                        : 'Not set'}
                    </td>
                    <td>{order.items.length}</td>
                    <td className="amount">
                      ZMK {order.items.reduce((sum, i) => sum + i.totalCost, 0).toFixed(2)}
                    </td>
                    <td>
                      <button 
                        className="update-btn"
                        onClick={() => openEditModal(order)}
                      >
                        Update Status
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredOrders.length > 0 && (
          <div className="pagination-bar">
            <div className="pagination-info">
              Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredOrders.length)} of {filteredOrders.length} parcels
            </div>

            <div className="pagination-controls">
              <button
                className="page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              >
                <RiArrowLeftLine />
              </button>

              <span className="page-number active">{currentPage}</span>

              <button
                className="page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              >
                <RiArrowRightLine />
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Edit Modal */}
      {selectedOrder && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Update Delivery - {selectedOrder.reference}</h2>
            <div className="modal-form">
              <label>Delivery Status</label>
              <select
                value={newDeliveryStatus}
                onChange={e => setNewDeliveryStatus(e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <label>Estimated Delivery Date</label>
              <input
                type="date"
                value={newEstimatedDate}
                onChange={e => setNewEstimatedDate(e.target.value)}
              />

              <div className="modal-actions">
                <button className="cancel-btn" onClick={closeModal}>
                  Cancel
                </button>
                <button className="save-btn" onClick={handleUpdateDelivery}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParcelManagement;
// import React from 'react';
// import { RiArchiveLine, RiArrowLeftLine, RiTruckLine, RiNavigationLine, RiCheckLine } from 'react-icons/ri';
// import './ParcelManagement.css';

// const ParcelManagement = () => {
//   const trackingEvents = [
//     {
//       time: 'Dec 17, 2024 at 8:30 AM',
//       title: 'Checkpoint updated: Out for Delivery',
//       by: 'Courier John Smith',
//       type: 'update',
//       dotColor: 'rgba(139, 126, 111, 1)',
//     },
//     {
//       time: 'Dec 17, 2024 at 8:00 AM',
//       title: 'Courier assigned: John Smith',
//       by: 'Admin',
//       type: 'assignment',
//       dotColor: 'rgba(203, 227, 195, 1)',
//     },
//     {
//       time: 'Dec 17, 2024 at 6:00 AM',
//       title: 'Checkpoint updated: Arrived at Hub',
//       by: 'System',
//       type: 'update',
//       dotColor: 'rgba(139, 126, 111, 0.6)',
//     },
//     {
//       time: 'Dec 16, 2024 at 5:20 PM',
//       title: 'Package shipped from warehouse',
//       by: 'System',
//       type: 'update',
//       dotColor: 'rgba(139, 126, 111, 0.6)',
//     },
//     {
//       time: 'Dec 15, 2024 at 11:15 AM',
//       title: 'Tracking ID generated',
//       by: 'Admin',
//       type: 'created',
//       dotColor: 'rgba(203, 227, 195, 1)',
//     },
//   ];

//   return (
//     <div className="parcel-management-page">
//       <header className="admin-header">
//         <div className="logo-section">
//           <div className="logo-icon">
//             <RiArchiveLine size={24} color="white" />
//           </div>
//           <span className="logo-text">OrderFlow</span>
//         </div>

//         <nav className="admin-nav">
//           <a href="#dashboard">Dashboard</a>
//           <a href="#orders">Orders</a>
//           <a href="#profile">Profile</a>
//         </nav>
//       </header>

//       <main className="parcel-main">
//         <div className="parcel-header">
//           <button className="back-btn">
//             <RiArrowLeftLine size={20} />
//           </button>
//           <div className="parcel-info">
//             <h1>Parcel #PAR-2024-001842-01</h1>
//             <p className="order-link">Order #ORD-2024-001842</p>
//           </div>
//         </div>

//         <section className="parcel-overview">
//           <div className="overview-card">
//             <h3>Current Status</h3>
//             <div className="current-status">
//               <div className="status-dot out-for-delivery" />
//               <div className="status-text">
//                 <strong>Out for Delivery</strong>
//                 <span>Since 8:30 AM • Midtown Hub</span>
//               </div>
//             </div>
//           </div>

//           <div className="overview-card">
//             <h3>Delivery Info</h3>
//             <div className="info-row">
//               <span className="label">Courier:</span>
//               <span>John Smith</span>
//             </div>
//             <div className="info-row">
//               <span className="label">Estimated Delivery:</span>
//               <span>Today, 4:00 – 6:00 PM</span>
//             </div>
//             <div className="info-row">
//               <span className="label">Last Updated:</span>
//               <span>8:30 AM today</span>
//             </div>
//           </div>
//         </section>

//         <section className="tracking-timeline">
//           <h2>Tracking History</h2>

//           <div className="timeline-container">
//             {trackingEvents.map((event, index) => (
//               <div key={index} className="timeline-event">
//                 <div className="timeline-dot" style={{ backgroundColor: event.dotColor }} />
//                 <div className="timeline-line" />
//                 <div className="event-content">
//                   <div className="event-title">{event.title}</div>
//                   <div className="event-time">{event.time}</div>
//                   <div className="event-by">by {event.by}</div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </section>

//         <section className="parcel-actions">
//           <button className="action-btn primary">
//             Refresh Status
//           </button>
//           <button className="action-btn">
//             Notify Customer
//           </button>
//           <button className="action-btn danger">
//             Report Issue
//           </button>
//         </section>
//       </main>
//     </div>
//   );
// };

// export default ParcelManagement;